/*
 * File: index.tsx
 * Desc: 表单容器组件
 * File Created: 2019-10-16 13:42:07
 * Author: chenghao at <hao.cheng@karakal.com.cn>
 * ------
 * Copyright 2019 - present, karakal
 */
import React, { useEffect, useImperativeHandle } from 'react';
import { Form } from 'antd';
import { FormProps } from 'antd/lib/form';
import classNames from 'classnames';
import { FormComponentProps, RcBaseFormProps } from 'antd/lib/form/Form';
import { usePrevious } from 'react-use';
import isEqual from 'react-fast-compare';
import {
    FormInput,
    FormDate,
    FormSelect,
    FormInputSearch,
    FormRadio,
    FormUpload,
    FormInputMultiple,
    FormCustom,
    FormCheckbox,
    FormGroup,
} from '..';

const FormComps: { [key: string]: any } = {
    input: FormInput,
    date: FormDate,
    select: FormSelect,
    inputSearch: FormInputSearch,
    radio: FormRadio,
    upload: FormUpload,
    inputMultiple: FormInputMultiple,
    custom: FormCustom,
    checkbox: FormCheckbox,
    group: FormGroup,
};

interface IField {
    label?: string;
    field?: string;
    /** 表单组件类型 */
    fieldType: string;
    width?: string | number;
    style?: React.CSSProperties;
    [key: string]: any;
}

interface FormContainerProps extends FormProps, RcBaseFormProps {
    fields: IField[];
    form: FormComponentProps['form'];
    data?: any;
    onValuesChange?: (changedValues: any, allValues: any) => void;
}

/** 表单容器组件 */
const FormContainer = (props: FormContainerProps, ref: any) => {
    const { data, labelCol, wrapperCol, form, ...rest } = props;
    const { setFieldsValue } = form;
    const prevData = usePrevious(data);
    const renderForm = (Component: any, field: IField) => {
        const _labelCol = { xs: { span: 24 }, sm: { span: 5 } },
            _wrapperCol = { xs: { span: 24 }, sm: { span: 12 } };
        const _props = {
            form: props.form,
            labelCol: labelCol || _labelCol,
            wrapperCol: wrapperCol || _wrapperCol,
            ...field,
        };
        return <Component {..._props} />;
    };
    useEffect(() => {
        if (!isEqual(data, prevData)) setFieldsValue(data);
    }, [data, prevData, setFieldsValue]);

    useImperativeHandle(ref, () => ({
        form: props.form,
    }));

    return (
        <Form {...rest} className={classNames('rac_fc', rest.className)}>
            {props.fields.map((field, index) => (
                // 默认宽度比例为50%
                <div
                    key={field.field || index}
                    style={{ flexBasis: field.width || '50%', ...field.style }}
                >
                    {renderForm(FormComps[field.fieldType], field)}
                </div>
            ))}
        </Form>
    );
};

export default Form.create<FormContainerProps>({
    onValuesChange: (props, changedValues, allValues) => {
        props.onValuesChange && props.onValuesChange(changedValues, allValues);
    },
})(React.memo(React.forwardRef(FormContainer)));
